const DEFAULT_CUTOFF_HOUR = 13;
const DEFAULT_WORKING_DAYS_BEFORE_CUTOFF = 2;
const DEFAULT_WORKING_DAYS_AFTER_CUTOFF = 3;
const DEFAULT_TIME_ZONE = 'Europe/Paris';
const DEFAULT_LOCALE = 'fr-FR';
const DEFAULT_VISIBLE_COUNTRY_CODES = [];
const DEFAULT_ESTIMATE_TEMPLATE = '[weekday] [date]';
const DEFAULT_CUTOFF_TEMPLATE = '[cutoff] - [countdown]';
const DEFAULT_CUTOFF_LABEL_TEMPLATE = '[cutoff]';
const DEFAULT_CUTOFF_TIME_TEMPLATE = '[hour]:00';
const NO_VISIBLE_COUNTRY_RESTRICTIONS = 0;

const COUNTRY_CODE_SEPARATOR = ',';
const DATE_PARTS_LOCALE = 'en-CA';
const HOUR_CYCLE_24 = 'h23';
const PLACEHOLDER_PATTERN = /\[([a-z_]+)\]/g;
const TRUE_ATTRIBUTE_VALUE = 'true';
const TWO_DIGIT = '2-digit';
const NUMERIC = 'numeric';
const UTC_TIME_ZONE = 'UTC';

const MILLISECONDS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;
const SECONDS_PER_HOUR = SECONDS_PER_MINUTE * MINUTES_PER_HOUR;
const TIMER_MIN_DELAY = MILLISECONDS_PER_SECOND;
const COUNTDOWN_PAD_LENGTH = 2;

const CALENDAR_MONTH_OFFSET = 1;
const NEXT_DAY_OFFSET = 1;
const SUNDAY_DAY_INDEX = 0;
const SATURDAY_DAY_INDEX = 6;
const MIDNIGHT_HOUR = 0;
const ZERO_MINUTES = 0;
const ZERO_SECONDS = 0;
const HOLIDAY_DATE_SEPARATOR = '-';

const FRANCE_PUBLIC_HOLIDAYS = {
  // Keep labels for DX while matching on ISO dates.
  '2026-01-01': "Jour de l'an",
  '2026-04-06': 'Lundi de Pâques',
  '2026-05-01': 'Fête du Travail',
  '2026-05-08': 'Victoire 1945',
  '2026-05-14': 'Ascension',
  '2026-05-25': 'Lundi de Pentecôte',
  '2026-07-14': 'Fête nationale',
  '2026-08-15': 'Assomption',
  '2026-11-01': 'Toussaint',
  '2026-11-11': 'Armistice 1918',
  '2026-12-25': 'Noël',
};

class DeliveryDate extends HTMLElement {
  /** @type {number | null} */
  timerId = null;

  /** @type {HTMLElement | null} */
  estimateElement = null;

  /** @type {HTMLElement | null} */
  cutoffElement = null;

  /** @type {HTMLElement | null} */
  separatorElement = null;

  /** @type {string | null} */
  estimateText = null;

  connectedCallback() {
    this.clearTimer();
    this.debugLog('connected', {
      source: this.source,
      currentCountryCode: this.currentCountryCode,
      visibleCountryCodes: this.visibleCountryCodes,
      rawCountryCode: this.getAttribute('country-code'),
      rawVisibleCountryCodes: this.getAttribute('visible-country-codes'),
      shopifyCountry: window.Shopify?.country || null,
      hiddenBeforeCheck: this.hidden,
    });

    const isVisible = this.isVisibleForCurrentCountry();
    this.debugLog('visibility-check', {
      source: this.source,
      currentCountryCode: this.currentCountryCode,
      visibleCountryCodes: this.visibleCountryCodes,
      isVisible,
    });

    if (!isVisible) {
      this.hidden = true;
      this.debugLog('hidden-after-visibility-check', {
        source: this.source,
        currentCountryCode: this.currentCountryCode,
        visibleCountryCodes: this.visibleCountryCodes,
      });
      return;
    }

    this.render();
    this.update();
    this.debugLog('rendered', {
      source: this.source,
      hiddenAfterRender: this.hidden,
      estimateTemplate: this.estimateTemplate,
      cutoffTemplate: this.cutoffTemplate,
    });
  }

  disconnectedCallback() {
    this.clearTimer();
    this.debugLog('disconnected', {
      source: this.source,
      currentCountryCode: this.currentCountryCode,
      visibleCountryCodes: this.visibleCountryCodes,
    });
  }

  clearTimer() {
    if (!this.timerId) return;

    window.clearTimeout(this.timerId);
    this.timerId = null;
  }

  isVisibleForCurrentCountry() {
    if (this.visibleCountryCodes.length === NO_VISIBLE_COUNTRY_RESTRICTIONS) return true;

    return this.visibleCountryCodes.includes(this.currentCountryCode);
  }

  get currentCountryCode() {
    return normalizeCountryCode(this.getAttribute('country-code') || window.Shopify?.country || '');
  }

  get visibleCountryCodes() {
    const countryCodes = this.getAttribute('visible-country-codes');

    if (!countryCodes) return DEFAULT_VISIBLE_COUNTRY_CODES;

    return countryCodes
      .split(COUNTRY_CODE_SEPARATOR)
      .map(normalizeCountryCode)
      .filter(Boolean);
  }

  get debug() {
    return this.getAttribute('debug') === TRUE_ATTRIBUTE_VALUE || window.location.search.includes('debugDeliveryDate=1');
  }

  get source() {
    return this.getAttribute('source') || 'default';
  }

  get cutoffHour() {
    return getNumberAttribute(this, 'cutoff-hour', DEFAULT_CUTOFF_HOUR);
  }

  get workingDaysBeforeCutoff() {
    return getNumberAttribute(this, 'working-days-before-cutoff', DEFAULT_WORKING_DAYS_BEFORE_CUTOFF);
  }

  get workingDaysAfterCutoff() {
    return getNumberAttribute(this, 'working-days-after-cutoff', DEFAULT_WORKING_DAYS_AFTER_CUTOFF);
  }

  get timeZone() {
    return this.getAttribute('time-zone') || DEFAULT_TIME_ZONE;
  }

  get locale() {
    return this.getAttribute('locale') || DEFAULT_LOCALE;
  }

  get estimateTemplate() {
    return this.getAttribute('estimate-template') || DEFAULT_ESTIMATE_TEMPLATE;
  }

  get cutoffTemplate() {
    return this.getAttribute('cutoff-template') || DEFAULT_CUTOFF_TEMPLATE;
  }

  get cutoffLabelTemplate() {
    return this.getAttribute('cutoff-label-template') || DEFAULT_CUTOFF_LABEL_TEMPLATE;
  }

  get cutoffTimeTemplate() {
    return this.getAttribute('cutoff-time-template') || DEFAULT_CUTOFF_TIME_TEMPLATE;
  }

  get forceBeforeCutoff() {
    return this.getAttribute('force-before-cutoff') === TRUE_ATTRIBUTE_VALUE;
  }

  get showCountdown() {
    const value = this.getAttribute('show-countdown');

    return value === null ? true : value === TRUE_ATTRIBUTE_VALUE;
  }

  get rangeMode() {
    return this.hasAttribute('range');
  }

  get rangeMinimumWorkingDays() {
    const attribute = this.isBeforeCutoff(getTimeZoneParts(new Date(), this.timeZone))
      ? 'range-min-working-days-before-cutoff'
      : 'range-min-working-days-after-cutoff';
    return getNumberAttribute(this, attribute, this.workingDaysBeforeCutoff);
  }

  get rangeMaximumWorkingDays() {
    const attribute = this.isBeforeCutoff(getTimeZoneParts(new Date(), this.timeZone))
      ? 'range-max-working-days-before-cutoff'
      : 'range-max-working-days-after-cutoff';
    return getNumberAttribute(this, attribute, this.workingDaysAfterCutoff);
  }

  get hideCutoff() {
    return this.getAttribute('hide-cutoff') === TRUE_ATTRIBUTE_VALUE;
  }

  render() {
    this.hidden = false;
    this.innerHTML = `
      <style>
        delivery-date {
          display: block;
        }

        delivery-date[hidden] {
          display: none;
        }

        .delivery-date {
          display: flex;
          flex-wrap: wrap;
          align-items: baseline;
          justify-content: center;
          column-gap: var(--gap-3xs, 0.375rem);
          width: 100%;
          color: var(--color-foreground);
          font-size: var(--font-size--sm, 0.875rem);
          line-height: 1.35;
        }

        .delivery-date__estimate {
          font-weight: 600;
        }

        .delivery-date__separator,
        .delivery-date__cutoff {
          color: rgb(var(--color-foreground-rgb) / var(--opacity-70, 0.7));
        }
      </style>
      <div class="delivery-date">
        <span class="delivery-date__estimate" data-delivery-estimate></span>
        <span class="delivery-date__separator" data-delivery-separator aria-hidden="true" hidden>-</span>
        <span class="delivery-date__cutoff" data-delivery-cutoff></span>
      </div>
    `;

    this.estimateElement = this.querySelector('[data-delivery-estimate]');
    this.separatorElement = this.querySelector('[data-delivery-separator]');
    this.cutoffElement = this.querySelector('[data-delivery-cutoff]');
  }

  update() {
    const now = new Date();
    const timeZoneParts = getTimeZoneParts(now, this.timeZone);
    const isBeforeCutoff = this.isBeforeCutoff(timeZoneParts);
    const workingDaysToAdd = isBeforeCutoff ? this.workingDaysBeforeCutoff : this.workingDaysAfterCutoff;
    const deliveryDate = addWorkingDays(timeZoneParts, workingDaysToAdd);
    let estimateText = formatTemplate(this.estimateTemplate, {
      weekday: formatWeekday(deliveryDate, this.locale),
      date: formatDayMonth(deliveryDate, this.locale),
    });

    if (this.rangeMode) {
      const range = calculateWorkingDayRange(
        timeZoneParts,
        this.rangeMinimumWorkingDays,
        this.rangeMaximumWorkingDays
      );
      const minimumDate = datePartsToDate(range.minimum);
      const maximumDate = datePartsToDate(range.maximum);
      estimateText = formatTemplate(this.estimateTemplate, {
        min_weekday: formatWeekday(minimumDate, this.locale),
        min_date: formatDayMonth(minimumDate, this.locale),
        max_weekday: formatWeekday(maximumDate, this.locale),
        max_date: formatDayMonth(maximumDate, this.locale),
      });
    }

    this.updateEstimate(estimateText);
    if (this.hideCutoff || this.rangeMode) {
      this.clearCutoff();
    } else {
      this.updateCutoff(now, timeZoneParts, isBeforeCutoff);
    }
    this.scheduleUpdate(now, timeZoneParts, isBeforeCutoff);
    this.debugLog('updated', {
      source: this.source,
      estimateText,
      isBeforeCutoff,
      workingDaysToAdd,
      timeZone: this.timeZone,
      locale: this.locale,
      hiddenAfterUpdate: this.hidden,
    });
  }

  clearCutoff() {
    if (this.separatorElement) this.separatorElement.hidden = true;
    if (this.cutoffElement) {
      this.cutoffElement.hidden = true;
      this.cutoffElement.textContent = '';
    }
  }

  /**
   * @param {string} event
   * @param {Record<string, unknown>} details
   */
  debugLog(event, details = {}) {
    if (!this.debug) return;

    console.debug('[delivery-date]', event, details, this);
  }

  /**
   * @param {{ year: number, month: number, day: number, hour: number }} timeZoneParts
   */
  isBeforeCutoff(timeZoneParts) {
    return this.forceBeforeCutoff || (isWorkingDay(datePartsToDate(timeZoneParts)) && timeZoneParts.hour < this.cutoffHour);
  }

  /**
   * @param {string} estimateText
   */
  updateEstimate(estimateText) {
    if (!this.estimateElement || this.estimateText === estimateText) return;

    this.estimateElement.textContent = estimateText;
    this.estimateText = estimateText;
  }

  /**
   * @param {Date} now
   * @param {{ year: number, month: number, day: number }} timeZoneParts
   * @param {boolean} isBeforeCutoff
   */
  updateCutoff(now, timeZoneParts, isBeforeCutoff) {
    if (!this.cutoffElement) return;

    if (!isBeforeCutoff) {
      this.cutoffElement.hidden = true;
      if (this.separatorElement) this.separatorElement.hidden = true;
      this.cutoffElement.textContent = '';
      return;
    }

    this.cutoffElement.hidden = false;
    if (this.separatorElement) this.separatorElement.hidden = false;
    const cutoff = formatCutoffHour(this.cutoffHour, this.cutoffTimeTemplate);

    this.cutoffElement.textContent = this.showCountdown
      ? formatTemplate(this.cutoffTemplate, {
          cutoff,
          countdown: formatCountdown(
            getCountdownTargetDate(now, timeZoneParts, this.cutoffHour, this.timeZone) - now.getTime()
          ),
        })
      : formatTemplate(this.cutoffLabelTemplate, {
          cutoff,
        });
  }

  /**
   * @param {Date} now
   * @param {{ year: number, month: number, day: number }} timeZoneParts
   * @param {boolean} isBeforeCutoff
   */
  scheduleUpdate(now, timeZoneParts, isBeforeCutoff) {
    const delay = isBeforeCutoff
      ? this.showCountdown
        ? MILLISECONDS_PER_SECOND
        : getNextMidnight(timeZoneParts, this.timeZone) - now.getTime()
      : getNextMidnight(timeZoneParts, this.timeZone) - now.getTime();

    this.clearTimer();
    this.timerId = window.setTimeout(() => this.update(), Math.max(TIMER_MIN_DELAY, delay));
  }
}

/**
 * @param {HTMLElement} element
 * @param {string} attribute
 * @param {number} fallback
 */
function getNumberAttribute(element, attribute, fallback) {
  const rawValue = element.getAttribute(attribute);
  if (rawValue == null || rawValue === '') return fallback;

  const value = Number(rawValue);

  return Number.isFinite(value) ? value : fallback;
}

/**
 * @param {string} countryCode
 */
function normalizeCountryCode(countryCode) {
  return countryCode.trim().toUpperCase();
}

/**
 * @param {string} template
 * @param {Record<string, string>} values
 */
function formatTemplate(template, values) {
  return template.replace(PLACEHOLDER_PATTERN, (placeholder, key) => values[key] ?? placeholder);
}

/**
 * @param {Date} date
 * @param {string} timeZone
 */
function getTimeZoneParts(date, timeZone) {
  const formatter = new Intl.DateTimeFormat(DATE_PARTS_LOCALE, {
    timeZone,
    year: NUMERIC,
    month: TWO_DIGIT,
    day: TWO_DIGIT,
    hour: TWO_DIGIT,
    minute: TWO_DIGIT,
    second: TWO_DIGIT,
    hourCycle: HOUR_CYCLE_24,
  });
  const parts = Object.fromEntries(formatter.formatToParts(date).map((part) => [part.type, part.value]));

  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour),
    minute: Number(parts.minute),
    second: Number(parts.second),
  };
}

/**
 * @param {{ year: number, month: number, day: number }} dateParts
 * @param {number} cutoffHour
 * @param {string} timeZone
 */
function getCutoffDate(dateParts, cutoffHour, timeZone) {
  return dateFromTimeZone(
    dateParts.year,
    dateParts.month,
    dateParts.day,
    cutoffHour,
    ZERO_MINUTES,
    ZERO_SECONDS,
    timeZone
  ).getTime();
}

/**
 * @param {Date} now
 * @param {{ year: number, month: number, day: number }} dateParts
 * @param {number} cutoffHour
 * @param {string} timeZone
 */
function getCountdownTargetDate(now, dateParts, cutoffHour, timeZone) {
  const todayCutoff = getCutoffDate(dateParts, cutoffHour, timeZone);

  if (todayCutoff > now.getTime()) return todayCutoff;

  return getNextCutoffDate(dateParts, cutoffHour, timeZone);
}

/**
 * @param {{ year: number, month: number, day: number }} dateParts
 * @param {number} cutoffHour
 * @param {string} timeZone
 */
function getNextCutoffDate(dateParts, cutoffHour, timeZone) {
  const nextDay = getNextCalendarDay(dateParts);

  return dateFromTimeZone(
    nextDay.getUTCFullYear(),
    nextDay.getUTCMonth() + CALENDAR_MONTH_OFFSET,
    nextDay.getUTCDate(),
    cutoffHour,
    ZERO_MINUTES,
    ZERO_SECONDS,
    timeZone
  ).getTime();
}

/**
 * @param {{ year: number, month: number, day: number }} dateParts
 * @param {string} timeZone
 */
function getNextMidnight(dateParts, timeZone) {
  const nextDay = getNextCalendarDay(dateParts);

  return dateFromTimeZone(
    nextDay.getUTCFullYear(),
    nextDay.getUTCMonth() + CALENDAR_MONTH_OFFSET,
    nextDay.getUTCDate(),
    MIDNIGHT_HOUR,
    ZERO_MINUTES,
    ZERO_SECONDS,
    timeZone
  ).getTime();
}

/**
 * @param {{ year: number, month: number, day: number }} dateParts
 */
function getNextCalendarDay(dateParts) {
  return new Date(Date.UTC(dateParts.year, dateParts.month - CALENDAR_MONTH_OFFSET, dateParts.day + NEXT_DAY_OFFSET));
}

/**
 * @param {{ year: number, month: number, day: number }} dateParts
 */
function datePartsToDate(dateParts) {
  return new Date(Date.UTC(dateParts.year, dateParts.month - CALENDAR_MONTH_OFFSET, dateParts.day));
}

/**
 * @param {number} year
 * @param {number} month
 * @param {number} day
 * @param {number} hour
 * @param {number} minute
 * @param {number} second
 * @param {string} timeZone
 */
function dateFromTimeZone(year, month, day, hour, minute, second, timeZone) {
  const utcDate = Date.UTC(year, month - CALENDAR_MONTH_OFFSET, day, hour, minute, second);
  const offset = getTimeZoneOffset(new Date(utcDate), timeZone);
  let result = new Date(utcDate - offset);
  const adjustedOffset = getTimeZoneOffset(result, timeZone);

  if (adjustedOffset !== offset) {
    result = new Date(utcDate - adjustedOffset);
  }

  return result;
}

/**
 * @param {Date} date
 * @param {string} timeZone
 */
function getTimeZoneOffset(date, timeZone) {
  const parts = getTimeZoneParts(date, timeZone);
  const utcDate = Date.UTC(
    parts.year,
    parts.month - CALENDAR_MONTH_OFFSET,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );

  return utcDate - date.getTime();
}

/**
 * @param {{ year: number, month: number, day: number }} dateParts
 * @param {number} workingDays
 */
function addWorkingDays(dateParts, workingDays) {
  const date = new Date(Date.UTC(dateParts.year, dateParts.month - CALENDAR_MONTH_OFFSET, dateParts.day));
  let remainingDays = workingDays;

  while (remainingDays > 0) {
    date.setUTCDate(date.getUTCDate() + NEXT_DAY_OFFSET);

    if (isWorkingDay(date)) {
      remainingDays -= NEXT_DAY_OFFSET;
    }
  }

  return date;
}

function calculateWorkingDayDate(dateParts, workingDays) {
  const date = addWorkingDays(dateParts, Math.max(0, Number(workingDays) || 0));

  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + CALENDAR_MONTH_OFFSET,
    day: date.getUTCDate(),
  };
}

function calculateWorkingDayRange(dateParts, minimumWorkingDays, maximumWorkingDays) {
  const minimum = Math.max(0, Number(minimumWorkingDays) || 0);
  const maximum = Math.max(minimum, Number(maximumWorkingDays) || 0);

  return {
    minimum: calculateWorkingDayDate(dateParts, minimum),
    maximum: calculateWorkingDayDate(dateParts, maximum),
  };
}

/**
 * @param {Date} date
 */
function isWorkingDay(date) {
  const day = date.getUTCDay();

  return day !== SUNDAY_DAY_INDEX && day !== SATURDAY_DAY_INDEX && !isHoliday(date);
}

/**
 * @param {Date} date
 */
function isHoliday(date) {
  return Object.prototype.hasOwnProperty.call(FRANCE_PUBLIC_HOLIDAYS, formatIsoDate(date));
}

/**
 * @param {Date} date
 */
function formatIsoDate(date) {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + CALENDAR_MONTH_OFFSET).padStart(COUNTDOWN_PAD_LENGTH, '0');
  const day = String(date.getUTCDate()).padStart(COUNTDOWN_PAD_LENGTH, '0');

  return [year, month, day].join(HOLIDAY_DATE_SEPARATOR);
}

/**
 * @param {Date} date
 * @param {string} locale
 */
function formatWeekday(date, locale) {
  return new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    timeZone: UTC_TIME_ZONE,
  }).format(date);
}

/**
 * @param {Date} date
 * @param {string} locale
 */
function formatDayMonth(date, locale) {
  return new Intl.DateTimeFormat(locale, {
    day: NUMERIC,
    month: 'long',
    timeZone: UTC_TIME_ZONE,
  }).format(date);
}

/**
 * @param {number} milliseconds
 */
function formatCountdown(milliseconds) {
  const totalSeconds = Math.max(ZERO_SECONDS, Math.floor(milliseconds / MILLISECONDS_PER_SECOND));
  const hours = Math.floor(totalSeconds / SECONDS_PER_HOUR);
  const minutes = Math.floor((totalSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  const seconds = totalSeconds % SECONDS_PER_MINUTE;

  return [hours, minutes, seconds].map((value) => String(value).padStart(COUNTDOWN_PAD_LENGTH, '0')).join(':');
}

/**
 * @param {number | string} hour
 * @param {string} template
 */
function formatCutoffHour(hour, template) {
  return formatTemplate(template, { hour: String(hour) });
}

if (!customElements.get('delivery-date')) {
  customElements.define('delivery-date', DeliveryDate);
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = { calculateWorkingDayDate, calculateWorkingDayRange, formatTemplate };
}
